import type { ITableCell, ITableSettings } from "@library-elements/table";

export interface IMergeSelection {
  startRow: number;
  startCol: number;
  endRow: number;
  endCol: number;
}

/** Returns true if the cell is hidden (merged into another cell). */
export const isCellMerged = (cell: ITableCell): boolean => {
  return !!cell.mergedInto;
};

/**
 * Resolve a set of selected cell ids to a mergeable region, or null.
 *
 * The selection MAY already contain merged cells. The region is the bounding
 * box of the selection expanded to the full extent of every merged cell in it,
 * and it is mergeable iff that box is tiled EXACTLY by the selected cells (and
 * their merge placeholders): no gaps, and no slot owned by a cell outside the
 * selection. So the result is always a rectangle containing all the selected
 * cells and only those. Rejects a single cell / a lone merged cell, a
 * gappy or non-rectangular selection, and a box that would pull in an
 * unselected cell. Used to enable/disable "Merge cells" and to gate the merge.
 */
export const getMergeableRegion = (
  draft: ITableSettings,
  cellIds: string[],
): IMergeSelection | null => {
  if (cellIds.length < 2) return null;
  const { tableData, tableCols } = draft;
  if (!tableData?.length || !tableCols) return null;

  const byId = new Map(tableData.map((c) => [c.id, c]));
  const indexById = new Map(tableData.map((c, i) => [c.id, i]));

  // Resolve every selected id to its OWNER (a placeholder → its parent) and
  // union the owners' full extents into a bounding box.
  const ownerIds = new Set<string>();
  let minRow = Number.POSITIVE_INFINITY;
  let maxRow = -1;
  let minCol = Number.POSITIVE_INFINITY;
  let maxCol = -1;
  for (const id of cellIds) {
    const cell = byId.get(id);
    if (!cell) return null;
    const owner = cell.mergedInto ? byId.get(cell.mergedInto) : cell;
    const ownerIdx = owner ? indexById.get(owner.id) : undefined;
    if (!owner || ownerIdx === undefined) return null;
    const row = Math.floor(ownerIdx / tableCols);
    const col = ownerIdx % tableCols;
    ownerIds.add(owner.id);
    minRow = Math.min(minRow, row);
    minCol = Math.min(minCol, col);
    maxRow = Math.max(maxRow, row + Math.max(0, (owner.rowSpan ?? 1) - 1));
    maxCol = Math.max(maxCol, col + Math.max(0, (owner.colSpan ?? 1) - 1));
  }

  // Every selection resolving to the same single cell → nothing to combine.
  if (ownerIds.size < 2) return null;

  // Every slot in the box must belong to a selected owner (directly or via a
  // placeholder). A slot owned by a cell outside the selection means the box
  // isn't tiled exactly by the selection → not a clean rectangle.
  for (let r = minRow; r <= maxRow; r++) {
    for (let c = minCol; c <= maxCol; c++) {
      const slot = tableData[r * tableCols + c];
      if (!slot) return null;
      const ownerId = slot.mergedInto ?? slot.id;
      if (!ownerIds.has(ownerId)) return null;
    }
  }

  return { startRow: minRow, startCol: minCol, endRow: maxRow, endCol: maxCol };
};

/**
 * Merge a region into a single cell. The top-left slot becomes the parent and
 * every other slot becomes its placeholder. Safe to call on a region that
 * already contains merged cells — prior parents/placeholders are reset and
 * re-pointed at the new parent (the top-left cell keeps its content, matching
 * standard merge behaviour).
 */
export const mergeCells = (
  draft: ITableSettings,
  sel: IMergeSelection,
): void => {
  const { startRow, startCol, endRow, endCol } = sel;
  const { tableData, tableCols } = draft;

  if (startRow === endRow && startCol === endCol) return;

  const topLeftIdx = startRow * tableCols + startCol;
  const parentCell = tableData[topLeftIdx];
  if (!parentCell) return;

  // The top-left slot may itself be a prior placeholder — promote it to parent.
  parentCell.mergedInto = undefined;
  parentCell.colSpan = endCol - startCol + 1;
  parentCell.rowSpan = endRow - startRow + 1;

  for (let r = startRow; r <= endRow; r++) {
    for (let c = startCol; c <= endCol; c++) {
      const idx = r * tableCols + c;
      if (idx === topLeftIdx) continue;
      const cell = tableData[idx];
      if (!cell) continue;
      cell.mergedInto = parentCell.id;
      cell.content = "";
      cell.colSpan = 1;
      cell.rowSpan = 1;
    }
  }
};

/** Unmerge a cell, resetting colSpan/rowSpan and clearing mergedInto references. */
export const unmergeCells = (
  draft: ITableSettings,
  cellId: string,
): void => {
  const { tableData } = draft;

  const parentCell = tableData.find((c) => c.id === cellId);
  if (!parentCell || (parentCell.colSpan <= 1 && parentCell.rowSpan <= 1))
    return;

  parentCell.colSpan = 1;
  parentCell.rowSpan = 1;

  for (const cell of tableData) {
    if (cell.mergedInto === cellId) {
      cell.mergedInto = undefined;
    }
  }
};

/**
 * Find the merge parent info for a cell that is a merge parent.
 * Returns { startRow, startCol, endRow, endCol } or null.
 */
const findMergeRegion = (
  draft: ITableSettings,
  parentIdx: number,
): IMergeSelection | null => {
  const { tableCols } = draft;
  const cell = draft.tableData[parentIdx];
  if (!cell || (cell.colSpan <= 1 && cell.rowSpan <= 1)) return null;

  const startRow = Math.floor(parentIdx / tableCols);
  const startCol = parentIdx % tableCols;
  return {
    startRow,
    startCol,
    endRow: startRow + cell.rowSpan - 1,
    endCol: startCol + cell.colSpan - 1,
  };
};

/**
 * Adjust merge regions when a row is about to be deleted.
 * Call BEFORE actually removing cells from the array.
 */
export const adjustMergesForRowDelete = (
  draft: ITableSettings,
  deletedRow: number,
): void => {
  const { tableData, tableCols } = draft;

  for (let idx = 0; idx < tableData.length; idx++) {
    const cell = tableData[idx];
    if (cell.colSpan <= 1 && cell.rowSpan <= 1) continue;
    if (cell.mergedInto) continue;

    const region = findMergeRegion(draft, idx);
    if (!region) continue;

    if (deletedRow < region.startRow || deletedRow > region.endRow) continue;

    if (deletedRow === region.startRow) {
      // Parent row is being deleted — transfer to next row
      if (cell.rowSpan > 1) {
        const newParentIdx = (region.startRow + 1) * tableCols + region.startCol;
        const newParent = tableData[newParentIdx];
        if (newParent) {
          newParent.mergedInto = undefined;
          newParent.content = cell.content;
          newParent.colSpan = cell.colSpan;
          newParent.rowSpan = cell.rowSpan - 1;

          for (const c of tableData) {
            if (c.mergedInto === cell.id) {
              c.mergedInto = newParent.id;
            }
          }

          if (newParent.colSpan <= 1 && newParent.rowSpan <= 1) {
            for (const c of tableData) {
              if (c.mergedInto === newParent.id) {
                c.mergedInto = undefined;
              }
            }
          }
        }
      }
      cell.colSpan = 1;
      cell.rowSpan = 1;
    } else {
      cell.rowSpan -= 1;
      if (cell.colSpan <= 1 && cell.rowSpan <= 1) {
        for (const c of tableData) {
          if (c.mergedInto === cell.id) {
            c.mergedInto = undefined;
          }
        }
      }
    }
  }

  for (let c = 0; c < tableCols; c++) {
    const cellIdx = deletedRow * tableCols + c;
    const cell = tableData[cellIdx];
    if (cell) {
      cell.mergedInto = undefined;
    }
  }
};

/**
 * Adjust merge regions when a column is about to be deleted.
 * Call BEFORE actually removing cells from the array.
 */
export const adjustMergesForColDelete = (
  draft: ITableSettings,
  deletedCol: number,
): void => {
  const { tableData, tableCols, tableRows } = draft;

  for (let idx = 0; idx < tableData.length; idx++) {
    const cell = tableData[idx];
    if (cell.colSpan <= 1 && cell.rowSpan <= 1) continue;
    if (cell.mergedInto) continue;

    const region = findMergeRegion(draft, idx);
    if (!region) continue;

    if (deletedCol < region.startCol || deletedCol > region.endCol) continue;

    if (deletedCol === region.startCol) {
      // Parent column is being deleted — transfer to next column
      if (cell.colSpan > 1) {
        const row = Math.floor(idx / tableCols);
        const newParentIdx = row * tableCols + region.startCol + 1;
        const newParent = tableData[newParentIdx];
        if (newParent) {
          newParent.mergedInto = undefined;
          newParent.content = cell.content;
          newParent.colSpan = cell.colSpan - 1;
          newParent.rowSpan = cell.rowSpan;

          for (const c of tableData) {
            if (c.mergedInto === cell.id) {
              c.mergedInto = newParent.id;
            }
          }

          if (newParent.colSpan <= 1 && newParent.rowSpan <= 1) {
            for (const c of tableData) {
              if (c.mergedInto === newParent.id) {
                c.mergedInto = undefined;
              }
            }
          }
        }
      }
      cell.colSpan = 1;
      cell.rowSpan = 1;
    } else {
      cell.colSpan -= 1;
      if (cell.colSpan <= 1 && cell.rowSpan <= 1) {
        for (const c of tableData) {
          if (c.mergedInto === cell.id) {
            c.mergedInto = undefined;
          }
        }
      }
    }
  }

  for (let r = 0; r < tableRows; r++) {
    const cellIdx = r * tableCols + deletedCol;
    const cell = tableData[cellIdx];
    if (cell) {
      cell.mergedInto = undefined;
    }
  }
};

/**
 * Adjust merge regions when a row is about to be inserted.
 * Call BEFORE actually inserting cells into the array.
 * insertedRow is the index where the new row will appear.
 */
export const adjustMergesForRowInsert = (
  draft: ITableSettings,
  insertedRow: number,
): void => {
  const { tableData } = draft;

  for (let idx = 0; idx < tableData.length; idx++) {
    const cell = tableData[idx];
    if (cell.colSpan <= 1 && cell.rowSpan <= 1) continue;
    if (cell.mergedInto) continue;

    const region = findMergeRegion(draft, idx);
    if (!region) continue;

    // The new row is inside the merge region (not at start, not past end)
    if (insertedRow > region.startRow && insertedRow <= region.endRow) {
      cell.rowSpan += 1;
    }
  }
};

/**
 * Adjust merge regions when a column is about to be inserted.
 * Call BEFORE actually inserting cells into the array.
 * insertedCol is the index where the new column will appear.
 */
export const adjustMergesForColInsert = (
  draft: ITableSettings,
  insertedCol: number,
): void => {
  const { tableData } = draft;

  for (let idx = 0; idx < tableData.length; idx++) {
    const cell = tableData[idx];
    if (cell.colSpan <= 1 && cell.rowSpan <= 1) continue;
    if (cell.mergedInto) continue;

    const region = findMergeRegion(draft, idx);
    if (!region) continue;

    if (insertedCol > region.startCol && insertedCol <= region.endCol) {
      cell.colSpan += 1;
    }
  }
};

/**
 * After inserting new cells, mark any that fall inside a merge region as mergedInto.
 * Call AFTER the splice that adds new cells to tableData.
 */
export const markInsertedCellsAsMerged = (
  draft: ITableSettings,
): void => {
  const { tableData, tableCols } = draft;

  const mergeRegions: { parentId: string; region: IMergeSelection }[] = [];
  for (let idx = 0; idx < tableData.length; idx++) {
    const cell = tableData[idx];
    if (cell.mergedInto) continue;
    if (cell.colSpan <= 1 && cell.rowSpan <= 1) continue;
    const region = findMergeRegion(draft, idx);
    if (region) {
      mergeRegions.push({ parentId: cell.id, region });
    }
  }

  for (let idx = 0; idx < tableData.length; idx++) {
    const cell = tableData[idx];
    if (cell.mergedInto) continue;
    if (cell.colSpan > 1 || cell.rowSpan > 1) continue;

    const row = Math.floor(idx / tableCols);
    const col = idx % tableCols;

    for (const { parentId, region } of mergeRegions) {
      if (cell.id === parentId) continue;
      if (
        row >= region.startRow &&
        row <= region.endRow &&
        col >= region.startCol &&
        col <= region.endCol
      ) {
        cell.mergedInto = parentId;
        cell.content = "";
        break;
      }
    }
  }
};
